'use client';

import { c as _c } from "react/compiler-runtime";
import { NEW_ACCOUNT_AGE_DAYS } from '@shared/all/domains/accountLabels';
import { ModerationReportType } from '@shared/all/domains/moderation';
import { FollowersListModalType } from '@shared/all/domains/profile';
import { type PublicUser } from '@shared/all/domains/user';
import AppWidgetContext from '@shared/utils/context/AppWidgetContext';
import AuthWidgetContext from '@shared/utils/context/AuthWidgetContext';
import { copyToClipboard } from '@shared/utils/helpers/clipboard';
import toast from '@shared/utils/helpers/toast';
import useRole from '@shared/utils/hooks/useRole';
import useUser from '@shared/utils/hooks/useUser';
import { useQueryClient } from '@tanstack/react-query';
import { History, Sparkles } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useContext, useState } from 'react';
import useContentUIRenderer from '../../hooks/useContentUIRenderer';
import { client } from '../../utils/trpc';
import Avatar from '../common/Avatar';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import PageBanner, { BANNER_ACTION_CLASS, BannerAction, BannerChip, BannerMenu, BannerStat } from '../common/PageBanner';
import PinnedFactiiis from '../common/PinnedFactiiis';
import { FactiiiCountModal } from '../factiiis/FactiiiCountModal';
import Modal, { ModalContent, ModalHeader } from '../HOC/Modal';
import OneDriveSyncButton from '../onedrive/OneDriveSyncButton';
import DmButton from '../pitches/DmButton';
import EmbedProfileModal from './EmbedProfileModal';
import ProfileEdit from './ProfileEdit';
import UserInfo from './UserInfo';

// Lazy: BotModal pulls in the frontend Wikipedia scraper (cheerio + bottleneck,
// ~350KB) — load it only when the bot-status modal actually opens.
const BotModal = dynamic(() => import('../scraper/BotModal'), {
  ssr: false
});
import UsersList from './UsersList';
type Props = {
  invalid?: boolean;
  loggedIn: boolean;
  userData: NonNullable<PublicUser>;
  refetch: () => void;
  isEmbed: boolean;
};
const ProfileSection = props => {
  const $ = _c(125);
  const {
    filterText
  } = useContentUIRenderer();
  const {
    currentUser
  } = useUser();
  const {
    isAdmin
  } = useRole(client);
  const {
    openAuthContinueModal
  } = useContext(AuthWidgetContext);
  const {
    newModerationReport
  } = useContext(AppWidgetContext);
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [botStatusModalOpen, setBotStatusModalOpen] = useState(false);
  const [showFollowersListModal, setShowFollowersListModal] = useState(null);
  const [showFactiiisCountModal, setShowFactiiisCountModal] = useState(false);
  const [profileOptionsDropdownOpen, setProfileOptionsDropdownOpen] = useState(false);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [editProfileModalOpen, setEditProfileModalOpen] = useState(false);
  const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);
  const {
    openLoginModal
  } = useContext(AuthWidgetContext);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== openAuthContinueModal || $[1] !== props || $[2] !== queryClient) {
    t0 = async () => {
      if (!props.loggedIn) {
        return openAuthContinueModal();
      }
      const [error] = await client.users.followUser.mutate({
        username: props.userData.username
      });
      if (error) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["users", "followCounts"]
      });
      props.refetch();
    };
    $[0] = openAuthContinueModal;
    $[1] = props;
    $[2] = queryClient;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const followHandler = t0;
  let t1;
  if ($[4] !== openAuthContinueModal || $[5] !== props || $[6] !== queryClient) {
    t1 = async () => {
      if (!props.loggedIn) {
        return openAuthContinueModal();
      }
      const [error_0] = await client.users.unfollowUser.mutate({
        username: props.userData.username
      });
      if (error_0) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: ["users", "followCounts"]
      });
      props.refetch();
    };
    $[4] = openAuthContinueModal;
    $[5] = props;
    $[6] = queryClient;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const unfollowHandler = t1;
  let t2;
  if ($[8] !== openAuthContinueModal || $[9] !== props.loggedIn) {
    t2 = type => () => {
      if (!props.loggedIn) {
        return openAuthContinueModal();
      }
      setShowFollowersListModal(type);
    };
    $[8] = openAuthContinueModal;
    $[9] = props.loggedIn;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const openFollowersListHandler = t2;
  let t3;
  if ($[11] !== props.userData.username) {
    t3 = async () => {
      await copyToClipboard(`${process.env.NEXT_PUBLIC_SITE_URL}/user/${props.userData.username}`);
      toast("Profile link copied to clipboard");
      setProfileOptionsDropdownOpen(false);
    };
    $[11] = props.userData.username;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const copyLinkHandler = t3;
  let t4;
  if ($[13] !== openAuthContinueModal || $[14] !== props) {
    t4 = async () => {
      if (!props.loggedIn) {
        return openAuthContinueModal();
      }
      if (props.userData.muted) {
        const [error_1] = await client.users.unmuteUser.mutate({
          username: props.userData.username
        });
        if (error_1) {
          return;
        }
        toast(`@${props.userData.username} unmuted`);
      } else {
        const [error_2] = await client.users.muteUser.mutate({
          username: props.userData.username
        });
        if (error_2) {
          return;
        }
        toast(`@${props.userData.username} muted`);
      }
      setProfileOptionsDropdownOpen(false);
      props.refetch();
    };
    $[13] = openAuthContinueModal;
    $[14] = props;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const muteButtonClickHandler = t4;
  let blockButtonClickHandler;
  let confirmBlock;
  if ($[16] !== openAuthContinueModal || $[17] !== props) {
    blockButtonClickHandler = () => {
      if (!props.loggedIn) {
        return openAuthContinueModal();
      }
      if (props.userData.blocked) {
        confirmBlock();
      } else {
        setProfileOptionsDropdownOpen(false);
        setBlockConfirmOpen(true);
      }
    };
    confirmBlock = async () => {
      if (props.userData.blocked) {
        const [error_3] = await client.users.unblockUser.mutate({
          username: props.userData.username
        });
        if (error_3) {
          return;
        }
        toast(`@${props.userData.username} unblocked`);
      } else {
        const [error_4] = await client.users.blockUser.mutate({
          username: props.userData.username
        });
        if (error_4) {
          return;
        }
        toast(`@${props.userData.username} blocked`);
      }
      setBlockConfirmOpen(false);
      setProfileOptionsDropdownOpen(false);
      props.refetch();
    };
    $[16] = openAuthContinueModal;
    $[17] = props;
    $[18] = blockButtonClickHandler;
    $[19] = confirmBlock;
  } else {
    blockButtonClickHandler = $[18];
    confirmBlock = $[19];
  }
  let t5;
  if ($[20] !== newModerationReport || $[21] !== openAuthContinueModal || $[22] !== props.loggedIn || $[23] !== props.userData.username) {
    t5 = () => {
      if (!props.loggedIn) {
        return openAuthContinueModal();
      }
      newModerationReport(ModerationReportType.USER, props.userData.username);
      setProfileOptionsDropdownOpen(false);
    };
    $[20] = newModerationReport;
    $[21] = openAuthContinueModal;
    $[22] = props.loggedIn;
    $[23] = props.userData.username;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  const reportClickHandler = t5;
  const t6 = props.userData.bio ?? "";
  let t7;
  if ($[25] !== filterText || $[26] !== t6) {
    t7 = filterText({
      content: t6,
      isFullLength: true
    });
    $[25] = filterText;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  const bio = t7;
  const t8 = `${props.userData.name || props.userData.username} banner`;
  const t9 = `${props.userData.username}-${props.userData.avatarKey ?? "default"}`;
  const t10 = props.userData?.tag === "BOT";
  let t11;
  if ($[28] !== props.userData.avatarKey || $[29] !== props.userData.id || $[30] !== props.userData.isFounder || $[31] !== props.userData.robohash || $[32] !== t10 || $[33] !== t9) {
    t11 = <Avatar key={t9} avatarKey={props.userData.avatarKey} robohash={props.userData.robohash} isBot={t10} isFounder={props.userData.isFounder} size={100} className="z-10 border-4 border-background-secondary" userId={props.userData.id} />;
    $[28] = props.userData.avatarKey;
    $[29] = props.userData.id;
    $[30] = props.userData.isFounder;
    $[31] = props.userData.robohash;
    $[32] = t10;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== currentUser.id || $[36] !== props.userData.id) {
    t12 = currentUser.id === props.userData.id && <Button text="Edit Profile" variant="CLEAR" classNames="px-2.5 py-1 text-xs" onClick={() => setEditProfileModalOpen(true)} />;
    $[35] = currentUser.id;
    $[36] = props.userData.id;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const t13 = props.userData.name || `@${props.userData.username}`;
  let t14;
  if ($[38] !== props.userData.isNew || $[39] !== props.userData.name || $[40] !== props.userData.username) {
    t14 = props.userData.name || props.userData.isNew ? <>{props.userData.name && <span>@{props.userData.username}</span>}{props.userData.isNew && <BannerChip icon={<Sparkles size={12} />} text="New account" title={`Joined less than ${NEW_ACCOUNT_AGE_DAYS} days ago`} dataId="new-account-chip" />}</> : undefined;
    $[38] = props.userData.isNew;
    $[39] = props.userData.name;
    $[40] = props.userData.username;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== currentUser.id || $[43] !== currentUser.isAuthenticated || $[44] !== isAdmin || $[45] !== openFollowersListHandler || $[46] !== openLoginModal || $[47] !== props.isEmbed || $[48] !== props.userData.followersCount || $[49] !== props.userData.followingCount || $[50] !== props.userData.id || $[51] !== props.userData.totalFactiiiCount || $[52] !== props.userData.username) {
    t15 = !props.isEmbed && <>{props.userData.totalFactiiiCount !== null && <BannerStat label="Factiiis" value={props.userData.totalFactiiiCount} onClick={() => setShowFactiiisCountModal(true)} />}<BannerStat label="Following" value={props.userData.followingCount} onClick={openFollowersListHandler(FollowersListModalType.FOLLOWING)} /><BannerStat label="Followers" value={props.userData.followersCount} onClick={openFollowersListHandler(FollowersListModalType.FOLLOWERS)} />{isAdmin && props.userData.username === "factiii-bot" && <BannerStat label="Scrapers" onClick={() => {
        if (!currentUser.isAuthenticated) {
          return openLoginModal();
        }
        setBotStatusModalOpen(true);
      }} />}{currentUser.id === props.userData.id && <span className="ml-auto"><OneDriveSyncButton /></span>}</>;
    $[42] = currentUser.id;
    $[43] = currentUser.isAuthenticated;
    $[44] = isAdmin;
    $[45] = openFollowersListHandler;
    $[46] = openLoginModal;
    $[47] = props.isEmbed;
    $[48] = props.userData.followersCount;
    $[49] = props.userData.followingCount;
    $[50] = props.userData.id;
    $[51] = props.userData.totalFactiiiCount;
    $[52] = props.userData.username;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  let t16;
  if ($[54] !== blockButtonClickHandler || $[55] !== copyLinkHandler || $[56] !== currentUser.id || $[57] !== currentUser.isAuthenticated || $[58] !== currentUser.username || $[59] !== followHandler || $[60] !== muteButtonClickHandler || $[61] !== profileOptionsDropdownOpen || $[62] !== props.isEmbed || $[63] !== props.userData.blocked || $[64] !== props.userData.blockedByUser || $[65] !== props.userData.following || $[66] !== props.userData.hiddenProfileHistory || $[67] !== props.userData.id || $[68] !== props.userData.muted || $[69] !== props.userData.username || $[70] !== reportClickHandler || $[71] !== unfollowHandler) {
    t16 = !props.isEmbed && <>{!props.userData.hiddenProfileHistory && <BannerAction href={`/user/${props.userData.username}/history`} icon={<History size={15} />} text="History" />}{currentUser.id !== props.userData.id && !props.userData.blocked && !props.userData.blockedByUser && <DmButton userId={props.userData.id} label="Message" className={BANNER_ACTION_CLASS} />}{!props.userData.following && currentUser.id !== props.userData.id && !props.userData.blocked && !props.userData.blockedByUser && <Button text="Follow" classNames="w-24" onClick={followHandler} />}{props.userData.following && !props.userData.blocked && !props.userData.blockedByUser && <Button text="Unfollow" classNames="w-24" variant="DANGER" onClick={unfollowHandler} />}{props.userData.blocked && <Button classNames="w-24" text="Unblock" variant="WARNING" onClick={blockButtonClickHandler} />}{currentUser.isAuthenticated && <BannerMenu label="Profile Options Dropdown" open={profileOptionsDropdownOpen} onToggle={() => setProfileOptionsDropdownOpen(!profileOptionsDropdownOpen)}><div className="absolute right-0 z-50 mt-2 w-48 rounded-md border border-ds-border-standard bg-background-primary p-2 shadow-md"><button className="block w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={copyLinkHandler}>Copy link to profile</button><button className="block w-full rounded-md px-4 py-2 text-left hover:bg-background-light md:hidden" onClick={() => setShowUserInfo(true)}>User Details</button><button className="block w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={() => setEmbedModalOpen(true)}>Embed Profile</button>{props.userData.username !== currentUser.username && <><button className="block w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={muteButtonClickHandler}>{props.userData.muted ? "Unmute" : "Mute"} @{props.userData.username}</button><button className="block w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={blockButtonClickHandler}>{props.userData.blocked ? "Unblock" : "Block"} @{props.userData.username}</button><button className="block w-full rounded-md px-4 py-2 text-left hover:bg-background-light" onClick={reportClickHandler}>Report @{props.userData.username}</button></>}</div></BannerMenu>}</>;
    $[54] = blockButtonClickHandler;
    $[55] = copyLinkHandler;
    $[56] = currentUser.id;
    $[57] = currentUser.isAuthenticated;
    $[58] = currentUser.username;
    $[59] = followHandler;
    $[60] = muteButtonClickHandler;
    $[61] = profileOptionsDropdownOpen;
    $[62] = props.isEmbed;
    $[63] = props.userData.blocked;
    $[64] = props.userData.blockedByUser;
    $[65] = props.userData.following;
    $[66] = props.userData.hiddenProfileHistory;
    $[67] = props.userData.id;
    $[68] = props.userData.muted;
    $[69] = props.userData.username;
    $[70] = reportClickHandler;
    $[71] = unfollowHandler;
    $[72] = t16;
  } else {
    t16 = $[72];
  }
  let t17;
  if ($[73] !== currentUser.id || $[74] !== props.userData.id || $[75] !== props.userData.pinnedFactiiis) {
    t17 = props.userData.pinnedFactiiis.length > 0 && <PinnedFactiiis factiiis={props.userData.pinnedFactiiis} canEdit={currentUser.id === props.userData.id} />;
    $[73] = currentUser.id;
    $[74] = props.userData.id;
    $[75] = props.userData.pinnedFactiiis;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== bio || $[78] !== props.userData.bannerKey || $[79] !== props.userData.username || $[80] !== t11 || $[81] !== t12 || $[82] !== t13 || $[83] !== t14 || $[84] !== t15 || $[85] !== t16 || $[86] !== t17 || $[87] !== t8) {
    t18 = <PageBanner variant="user" entityKey={props.userData.username} bannerKey={props.userData.bannerKey} bannerAlt={t8} avatar={t11} coverActions={t12} title={t13} subtitle={t14} description={bio} stats={t15} actions={t16}>{t17}</PageBanner>;
    $[77] = bio;
    $[78] = props.userData.bannerKey;
    $[79] = props.userData.username;
    $[80] = t11;
    $[81] = t12;
    $[82] = t13;
    $[83] = t14;
    $[84] = t15;
    $[85] = t16;
    $[86] = t17;
    $[87] = t8;
    $[88] = t18;
  } else {
    t18 = $[88];
  }
  let t19;
  if ($[89] !== props.userData.username || $[90] !== showFollowersListModal) {
    t19 = showFollowersListModal !== null && <Modal closeHandler={() => setShowFollowersListModal(null)}><ModalHeader closeHandler={() => setShowFollowersListModal(null)}><h2>Followers</h2></ModalHeader><ModalContent><UsersList title={showFollowersListModal.toString()} type={showFollowersListModal} username={props.userData.username} fetchFunction={cursor => showFollowersListModal === FollowersListModalType.FOLLOWERS ? client.users.followersList.query({
          username: props.userData.username,
          cursor
        }) : client.users.followingList.query({
          username: props.userData.username,
          cursor
        })} /></ModalContent></Modal>;
    $[89] = props.userData.username;
    $[90] = showFollowersListModal;
    $[91] = t19;
  } else {
    t19 = $[91];
  }
  let t20;
  if ($[92] !== props.userData.username || $[93] !== showFactiiisCountModal) {
    t20 = showFactiiisCountModal && <FactiiiCountModal username={props.userData.username} closeHandler={() => setShowFactiiisCountModal(false)} />;
    $[92] = props.userData.username;
    $[93] = showFactiiisCountModal;
    $[94] = t20;
  } else {
    t20 = $[94];
  }
  let t21;
  if ($[95] !== profileOptionsDropdownOpen) {
    t21 = profileOptionsDropdownOpen && <div className="fixed inset-0 z-0" onClick={() => setProfileOptionsDropdownOpen(false)} />;
    $[95] = profileOptionsDropdownOpen;
    $[96] = t21;
  } else {
    t21 = $[96];
  }
  let t22;
  if ($[97] !== embedModalOpen || $[98] !== props.userData.username) {
    t22 = embedModalOpen && <EmbedProfileModal closeHandler={() => setEmbedModalOpen(false)} username={props.userData.username} />;
    $[97] = embedModalOpen;
    $[98] = props.userData.username;
    $[99] = t22;
  } else {
    t22 = $[99];
  }
  let t23;
  if ($[100] !== botStatusModalOpen) {
    t23 = botStatusModalOpen && <BotModal closeHandler={() => setBotStatusModalOpen(false)} />;
    $[100] = botStatusModalOpen;
    $[101] = t23;
  } else {
    t23 = $[101];
  }
  let t24;
  if ($[102] !== t18 || $[103] !== t19 || $[104] !== t20 || $[105] !== t21 || $[106] !== t22 || $[107] !== t23) {
    t24 = <div className="w-full">{t18}{t19}{t20}{t21}{t22}{t23}</div>;
    $[102] = t18;
    $[103] = t19;
    $[104] = t20;
    $[105] = t21;
    $[106] = t22;
    $[107] = t23;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  let t25;
  if ($[109] !== editProfileModalOpen || $[110] !== props.refetch || $[111] !== props.userData) {
    t25 = editProfileModalOpen && props.userData && <ProfileEdit closeHandler={() => setEditProfileModalOpen(false)} initialUserData={props.userData} refetch={props.refetch} />;
    $[109] = editProfileModalOpen;
    $[110] = props.refetch;
    $[111] = props.userData;
    $[112] = t25;
  } else {
    t25 = $[112];
  }
  let t26;
  if ($[113] !== props.userData || $[114] !== showUserInfo) {
    t26 = showUserInfo && props.userData && <Modal closeHandler={() => setShowUserInfo(false)}><ModalHeader><h2>User Info</h2></ModalHeader><UserInfo publicUser={props.userData} /></Modal>;
    $[113] = props.userData;
    $[114] = showUserInfo;
    $[115] = t26;
  } else {
    t26 = $[115];
  }
  let t27;
  if ($[116] !== blockConfirmOpen || $[117] !== confirmBlock || $[118] !== props.userData.username) {
    t27 = blockConfirmOpen && <Confirmation title={`Block @${props.userData.username}?`} message={`They will not be able to follow you or view your posts, and you will not see posts or notifications from @${props.userData.username}.`} cancelHandler={() => setBlockConfirmOpen(false)} confirmHandler={confirmBlock} actionButtonText="Block" variant="DANGER" />;
    $[116] = blockConfirmOpen;
    $[117] = confirmBlock;
    $[118] = props.userData.username;
    $[119] = t27;
  } else {
    t27 = $[119];
  }
  let t28;
  if ($[120] !== t24 || $[121] !== t25 || $[122] !== t26 || $[123] !== t27) {
    t28 = <div className="flex">{t24}{t25}{t26}{t27}</div>;
    $[120] = t24;
    $[121] = t25;
    $[122] = t26;
    $[123] = t27;
    $[124] = t28;
  } else {
    t28 = $[124];
  }
  return t28;
};
export default ProfileSection;